import React, {useEffect, useRef, useState} from 'react';
import {Animated, Platform, TouchableOpacity} from 'react-native';
import {useIsFocused, useNavigation} from '@react-navigation/native';
import {RFValue} from 'react-native-responsive-fontsize';

import {useAuth} from '../../../context/auth';
import {useHelpers} from '../../../context/helpers';
import {api} from '../../../services/api';
import {Piggy} from '../../Interface/Piggy';
import {PromoMarqueeModal} from '../PromoMarqueeModal';

import shieldIcon from '../../../assets/images/icons/shield.webp';

import {
  AmountTitle,
  PiggyContainer,
  SavedContainer,
  SavedContainerFooterInfo,
  SavedContainerFooterInfoText,
  SavedContent as SavedContentRow,
  SavedContentContainer,
  SavedIcon,
  SavedTitle,
} from './styles';

type FooterBannerAction = 'menorPreco' | 'indiqueAmigos';

const FOOTER_BANNER_ITEMS: {
  id: number;
  text: string;
  action: FooterBannerAction;
}[] = [
  {id: 1, text: 'O MENOR preço ou seu dinheiro de volta!', action: 'menorPreco'},
  {id: 2, text: 'Indique amigos e ganhe mais descontos!', action: 'indiqueAmigos'},
];

const BANNER_DISPLAY_DURATION = 3500;
const BANNER_ANIMATION_DURATION = 300;

type SavedContentProps = {
  showFooterBanner?: boolean;
};

export function SavedContent({showFooterBanner = true}: SavedContentProps) {
  const {isLogged, setShowSign} = useAuth();
  const {formatNumberToLocaleAmount} = useHelpers();
  const isFocused = useIsFocused();
  const navigation = useNavigation();

  const [valorEconomizado, setValorEconomizado] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [showPromoMarqueeModal, setShowPromoMarqueeModal] = useState(false);

  const bannerAnim = useRef(new Animated.Value(0)).current;
  const bannerAnimRef = useRef<Animated.CompositeAnimation | null>(null);
  const [bannerIndex, setBannerIndex] = useState(0);

  useEffect(() => {
    let isMounted = true;

    async function buscaValorEconomizado() {
      await api
        .get('/consultaValorEconomizado')
        .then(({data}) => {
          if (isMounted) {
            setValorEconomizado(data);
          }
        })
        .catch(() => {})
        .finally(() => {
          if (isMounted) {
            setIsLoading(false);
          }
        });
    }

    if (isLogged) {
      buscaValorEconomizado();
    } else {
      if (isMounted) {
        setValorEconomizado(0);
        setIsLoading(false);
        isMounted = false;
      }
    }

    return () => {
      isMounted = false;
    };
  }, [isFocused, isLogged]);

  useEffect(() => {
    if (!showFooterBanner) {
      return;
    }

    bannerAnimRef.current?.stop();
    bannerAnim.setValue(0);

    const sequence = Animated.sequence([
      Animated.timing(bannerAnim, {
        toValue: 1,
        duration: BANNER_ANIMATION_DURATION,
        useNativeDriver: true,
      }),
      // Animated.delay usa useNativeDriver: false internamente (fixo no
      // react-native) e cria um interaction handle a cada ciclo; como esse
      // efeito se retrigger sozinho, isso travava o InteractionManager pra
      // sempre. Timing "parado" no mesmo valor com useNativeDriver: true
      // produz a mesma espera sem esse efeito colateral.
      Animated.timing(bannerAnim, {
        toValue: 1,
        duration: BANNER_DISPLAY_DURATION,
        useNativeDriver: true,
      }),
      Animated.timing(bannerAnim, {
        toValue: 2,
        duration: BANNER_ANIMATION_DURATION,
        useNativeDriver: true,
      }),
    ]);

    bannerAnimRef.current = sequence;
    bannerAnimRef.current.start(({finished}) => {
      if (!finished) {
        return;
      }
      setBannerIndex(prev => (prev + 1) % FOOTER_BANNER_ITEMS.length);
    });

    return () => bannerAnimRef.current?.stop();
  }, [showFooterBanner, bannerAnim, bannerIndex]);

  const bannerTranslateY = bannerAnim.interpolate({
    inputRange: [0, 1, 2],
    outputRange: [10, 0, -10],
  });

  const bannerOpacity = bannerAnim.interpolate({
    inputRange: [0, 1, 2],
    outputRange: [0, 1, 0],
  });

  function handleFooterBannerPress() {
    const {action} = FOOTER_BANNER_ITEMS[bannerIndex];

    if (action === 'menorPreco') {
      setShowPromoMarqueeModal(true);
      return;
    }

    if (!isLogged) {
      setShowSign(true);
      return;
    }
    navigation.navigate('ReferAndWin' as never);
  }

  return (
    <>
      <SavedContainer>
        <SavedContentRow>
          <SavedContentContainer>
            <SavedTitle>Economizei com o baratão:</SavedTitle>
            <AmountTitle>
              {isLoading
                ? 'R$ ••••'
                : typeof valorEconomizado === 'number'
                ? formatNumberToLocaleAmount(valorEconomizado)
                : 'R$ ••••'}
            </AmountTitle>
          </SavedContentContainer>
          <PiggyContainer>
            <Piggy width={RFValue(50)} height={RFValue(50)} />
          </PiggyContainer>
        </SavedContentRow>
      </SavedContainer>

      {showFooterBanner && Platform.OS !== 'web' && (
        <>
          <SavedContainerFooterInfo>
            <TouchableOpacity
              activeOpacity={0.7}
              onPress={handleFooterBannerPress}>
              <Animated.View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: RFValue(4),
                  transform: [{translateY: bannerTranslateY}],
                  opacity: bannerOpacity,
                }}>
                <SavedIcon source={shieldIcon} />
                <SavedContainerFooterInfoText>
                  {FOOTER_BANNER_ITEMS[bannerIndex].text}
                </SavedContainerFooterInfoText>
              </Animated.View>
            </TouchableOpacity>
          </SavedContainerFooterInfo>

           <PromoMarqueeModal
            isOpen={showPromoMarqueeModal}
            onClose={() => setShowPromoMarqueeModal(false)}
          />

        </>
      )}
    </>
  );
}
